import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger, } from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import { cn } from "../../lib/utils";
import { DateTime } from "./DateTime";
function getDate(date, offset, space, round) {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() + offset);
    if (!space) {
        return d;
    }
    if (round === "up") {
        d.setSeconds(0);
        d.setMinutes(space * Math.ceil(d.getMinutes() / space));
    }
    else if (round === "down") {
        d.setSeconds(0);
        d.setMinutes(space * Math.floor(d.getMinutes() / space));
    }
    return d;
}
function toDigits(n) {
    return (~~n).toString().padStart(2, "0");
}
const TimePicker = ({ value, onChange, readOnly, space, }) => {
    const items = React.useMemo(() => {
        const freq = 60 / space;
        return Array.from({ length: 24 * freq }).map((_, id) => ({
            id,
            value: `${toDigits(id / freq)}:${toDigits((id % freq) * space)}`,
        }));
    }, [space]);
    const hasSpecial = React.useMemo(() => value && !items.some((q) => q.value === value), [value, items]);
    return (React.createElement("div", { className: "p-3 border-t" },
        React.createElement(Select, { value: value, onValueChange: onChange, disabled: readOnly },
            React.createElement(SelectTrigger, { className: "w-full" },
                React.createElement(SelectValue, { placeholder: "Uhrzeit ausw\u00E4hlen" })),
            React.createElement(SelectContent, null,
                hasSpecial && React.createElement(SelectItem, { value: value }, value),
                items.map((q) => (React.createElement(SelectItem, { key: q.id, value: q.value }, q.value)))))));
};
export const DateTimePicker = ({ value, min, onChange, readOnly, space, className, withTime, }) => {
    const [open, setOpen] = React.useState(false);
    const time = React.useMemo(() => {
        const current = (value === null || value === void 0 ? void 0 : value.toTimeString().substring(0, 5)) || "";
        if (current) {
            const [hours, minutes] = current.split(":");
            return `${hours}:${minutes}`;
        }
        return "";
    }, [value]);
    const changeDateTime = (date) => {
        if (!date) {
            // empty on purpose
        }
        else if (min && date < min) {
            onChange(getDate(min, 0, space, "up"));
        }
        else {
            onChange(date);
        }
    };
    const changeDate = (date) => {
        if (date) {
            if (value) {
                date.setHours(value.getHours(), value.getMinutes(), 0, 0);
            }
            else {
                date.setHours(8, 0, 0, 0);
            }
            changeDateTime(date);
        }
    };
    const changeTime = (time) => {
        if (!withTime) {
            return;
        }
        const [hours, minutes] = time.split(":");
        const nh = Number.parseInt(hours, 10);
        const nm = Number.parseInt(minutes, 10);
        if (!isNaN(nh) && !isNaN(nm)) {
            const date = new Date(value !== null && value !== void 0 ? value : Date.now());
            date.setHours(nh, nm, 0, 0);
            changeDateTime(date);
        }
    };
    return (React.createElement(Popover, { open: open, onOpenChange: setOpen },
        React.createElement(PopoverTrigger, { asChild: true },
            React.createElement("div", null,
                React.createElement(Button, { type: "button", variant: "outline", disabled: readOnly, className: cn("w-[200px] pl-3 text-left font-normal", className) },
                    value ? (React.createElement(DateTime, { value: value.toISOString(), onlyDate: !withTime, onlyAbsolute: true })) : (React.createElement("span", null, withTime ? "Zeit auswählen" : "Datum auswählen")),
                    React.createElement(CalendarIcon, { className: "ml-auto h-4 w-4" })))),
        React.createElement(PopoverContent, { className: "w-auto p-0", align: "start" },
            React.createElement(Calendar, { mode: "single", selected: value, defaultMonth: value, fromDate: min, disabled: readOnly, onSelect: changeDate }),
            withTime && (React.createElement(TimePicker, { onChange: changeTime, value: time, readOnly: readOnly, space: space })))));
};
//# sourceMappingURL=DateTimePicker.js.map